import { useEffect, useState } from 'react';
import type { FilterBreakdown, SessionFilters } from '@filmsloth/shared';
import { api } from '../api/client.js';

/**
 * How many titles the current filters leave, and which filter to loosen if
 * that number is disappointing.
 *
 * Shared by the create screen and the lobby so both give the same answer:
 * tightening filters in the waiting room used to be blind, and the host only
 * found out what it cost after saving.
 *
 * Requests are debounced, because every chip tap changes the filters, and a
 * stale response is dropped rather than allowed to overwrite a newer one.
 */
export function useFilterPreview(filters: SessionFilters, enabled = true): FilterBreakdown | null {
  const [preview, setPreview] = useState<FilterBreakdown | null>(null);

  // The filter object is rebuilt on every change, so the effect keys off its
  // contents rather than its identity.
  const key = JSON.stringify(filters);

  useEffect(() => {
    // No provider means no deck, and the server would only confirm zero.
    if (!enabled || filters.providers.length === 0) {
      setPreview(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .describeFilters(filters)
        .then((result) => {
          if (!cancelled) setPreview(result);
        })
        .catch(() => {
          // A failed count is not worth an error message: the button below
          // still works, and the next keystroke tries again.
          if (!cancelled) setPreview(null);
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return preview;
}
